import Decimal from "decimal.js";
import EstimateItemModel from "@/usecases/orders/estimate-item";
import Estimate from "@/usecases/orders/estimate";

export interface ExpenseCategoryTotal {
  expenseCategoryId: string;
  expenseCategoryName: string;
  total: number;
}

export function calculateExpenseCategory(
  estimate: Estimate,
  expenseCategoryOptions: any[],
  categoryDiscountIds: string[]
): ExpenseCategoryTotal[] {
  let categories: ExpenseCategoryTotal[] = [];
  let categoryOptions = expenseCategoryOptions;

  // Process estimate items
  processExpenseCategory(
    estimate.items,
    categoryOptions,
    categories,
    categoryDiscountIds
  );
  // Process estimate children
  estimate.children.forEach(child => {
    processExpenseCategory(
      child.items,
      categoryOptions,
      categories,
      categoryDiscountIds
    );
  });

  // Move 値引き to the end of the array
  if (
    categories.some(category =>
      categoryDiscountIds.includes(category.expenseCategoryId)
    )
  ) {
    const categoriesDiscount = categories.filter(category =>
      categoryDiscountIds.includes(category.expenseCategoryId)
    );
    categories = categories
      .filter(
        category => !categoryDiscountIds.includes(category.expenseCategoryId)
      )
      .concat(categoriesDiscount);
  }

  return getExpensesWithDiscount(categories);
}

function getExpensesWithDiscount(
  expenses: ExpenseCategoryTotal[]
): ExpenseCategoryTotal[] {
  // Get index of discount
  const discountIndexes: number[] = [];
  expenses.forEach((expense, index) => {
    if (expense.expenseCategoryName === "値引き") {
      discountIndexes.push(index);
    }
  });

  if (discountIndexes.length === 0) {
    return expenses;
  }

  // Calculate the total discount value using the provided indexes
  const discount = discountIndexes.reduce(
    (acc, index) => acc + expenses[index].total,
    0
  );

  // Extract the cost items and their original indexes
  const costItems = expenses
    .map((item, index) => (discountIndexes.includes(index) ? null : item))
    .filter(item => item != null);

  // Calculate total cost and the proportion of discount for each item
  const totalCost = costItems.reduce((acc, curr) => {
    if (acc === null || curr === null) {
      return 0;
    }
    return acc + curr.total;
  }, 0);
  const discountShares = costItems.map(item => {
    if (item === null || totalCost === null || totalCost === 0) {
      return 0;
    }
    return new Decimal(item.total)
      .div(totalCost)
      .mul(discount)
      .toNumber();
  });

  let costIndex = 0;
  // Create a new array with the discounted values but maintain the original positions
  return expenses.map((item, index) => {
    if (discountIndexes.includes(index)) {
      return item;
    } else {
      const discountedValue = new Decimal(item.total)
        .plus(discountShares[costIndex])
        .toNumber();
      costIndex += 1;
      return { ...item, total: discountedValue };
    }
  });
}

function processExpenseCategory(
  items: EstimateItemModel[],
  options: any[],
  result: ExpenseCategoryTotal[],
  categoryDiscountIds: string[]
) {
  calculate(items, options, result, categoryDiscountIds);
  items.forEach(item => {
    if (item.items && item.items.length > 0) {
      processExpenseCategory(item.items, options, result, categoryDiscountIds);
    }
  });
}

function calculate(
  items: EstimateItemModel[],
  options: any[],
  result: ExpenseCategoryTotal[],
  categoryDiscountIds: string[]
) {
  for (let i = 0; i < items.length; i++) {
    const isDiscount = categoryDiscountIds.includes(items[i].expenseCategoryId);
    const value = isDiscount ? items[i].totalPrice : items[i].loss;
    if (
      (items[i].expenseCategoryId !== "" && items[i].loss !== 0) ||
      (isDiscount && items[i].totalPrice !== 0)
    ) {
      const name = options
        .filter(option => option.value === items[i].expenseCategoryId)
        .map(option => option.text);
      const categoryExists = result.find(
        item => item.expenseCategoryId === items[i].expenseCategoryId
      );

      if (!categoryExists) {
        result.push({
          expenseCategoryId: items[i].expenseCategoryId,
          expenseCategoryName: name.length === 0 ? "" : name[0],
          total: value
        });
      } else {
        categoryExists.total += value;
      }
    }
  }
}
